/**
 * Import d'un planning Excel — la reconnaissance des salariés (§5).
 *
 * Le fichier écrit `NOM PRENOM` en majuscules, souvent avec une espace finale (`SENG PHILIPPE `), et
 * certains noms comptent deux mots de nom de famille (`MBOCK HANG Julienne`, `CHAUMONTJEANDAT Darryl`).
 *
 * **Rien n'est jamais deviné.** Un nom non reconnu voit ses lignes écartées ; il n'est pas créé comme
 * nouveau salarié, et aucun de ses créneaux n'est écrit.
 */
import type { Rapprochement, NiveauRapprochement } from './types';
import { normaliser } from './lireCellule';

/** Un salarié de l'IAmani, réduit à ce dont le rapprochement a besoin. */
export interface SalarieIAmani {
    id: string;
    first_name?: string;
    last_name?: string;
}

/** Un rapprochement confirmé lors d'un import précédent (table `import_alias_salarie`). */
export interface AliasSalarie {
    nom_fichier: string;
    employee_id: string;
}

/** `LASTNAME Firstname`, la forme sous laquelle le planning affiche un salarié. */
export function nomAffiche(salarie: SalarieIAmani): string {
    return `${(salarie.last_name || '').toUpperCase()} ${salarie.first_name || ''}`.trim();
}

/** Les mots d'un nom, normalisés et triés — pour comparer `NOM PRENOM` et `PRENOM NOM`. */
function motsTries(nom: string): string {
    return normaliser(nom).split(' ').filter(Boolean).sort().join(' ');
}

/**
 * Rapproche chaque nom du fichier d'un salarié de l'IAmani, dans l'ordre du §5.
 *
 * 1. un alias confirmé lors d'un import précédent — la deuxième semaine importée ne repose plus la question
 * 2. égalité stricte après normalisation
 * 3. égalité sur l'ensemble des mots, ordre ignoré
 * 4. correspondance unique sur le nom de famille seul → **proposée**, à confirmer, jamais appliquée seule
 * 5. rien → la ligne sera ignorée, le nom listé dans le récapitulatif
 */
export function rapprocherSalaries(
    nomsFichier: { nomFichier: string; ligneExcel: number }[],
    salaries: SalarieIAmani[],
    alias: AliasSalarie[] = [],
): Rapprochement[] {
    const parAlias = new Map(alias.map((a) => [normaliser(a.nom_fichier), a.employee_id]));
    const parId = new Map(salaries.map((s) => [String(s.id), s]));

    const parNomComplet = new Map<string, SalarieIAmani>();
    const parMots = new Map<string, SalarieIAmani>();
    // Un nom de famille partagé par deux salariés ne peut plus servir à trancher : on le neutralise.
    const parNomFamille = new Map<string, SalarieIAmani | null>();

    for (const salarie of salaries) {
        const complet = normaliser(nomAffiche(salarie));
        if (complet) parNomComplet.set(complet, salarie);
        const mots = motsTries(nomAffiche(salarie));
        if (mots) parMots.set(mots, salarie);
        const famille = normaliser(salarie.last_name || '');
        if (famille) parNomFamille.set(famille, parNomFamille.has(famille) ? null : salarie);
    }

    return nomsFichier.map(({ nomFichier, ligneExcel }) => {
        const normalise = normaliser(nomFichier);
        const retenir = (salarie: SalarieIAmani, niveau: NiveauRapprochement): Rapprochement => ({
            nomFichier,
            ligneExcel,
            niveau,
            employeeId: String(salarie.id),
            employeeNom: nomAffiche(salarie),
            // Une correspondance sur le seul nom de famille est proposée décochée : c'est à l'utilisateur
            // de la confirmer à l'étape 2, sans quoi l'import inventerait un rapprochement.
            retenu: niveau !== 'aConfirmer',
        });

        const idAlias = parAlias.get(normalise);
        const salarieAlias = idAlias ? parId.get(String(idAlias)) : undefined;
        if (salarieAlias) return retenir(salarieAlias, 'alias');

        const exact = parNomComplet.get(normalise);
        if (exact) return retenir(exact, 'exact');

        const mots = parMots.get(motsTries(nomFichier));
        if (mots) return retenir(mots, 'mots');

        // Le nom de famille seul : uniquement si le fichier n'écrit qu'un mot, ou si l'un des mots
        // désigne sans ambiguïté un salarié unique.
        const motsDuFichier = normalise.split(' ').filter(Boolean);
        const candidats = motsDuFichier
            .map((mot) => parNomFamille.get(mot))
            .filter((s): s is SalarieIAmani => Boolean(s));
        const uniques = new Map(candidats.map((s) => [String(s.id), s]));
        if (uniques.size === 1) return retenir([...uniques.values()][0], 'aConfirmer');

        return { nomFichier, ligneExcel, niveau: 'inconnu', retenu: false };
    });
}

/** Le bilan des trois tuiles de l'étape 2 : reconnus, à confirmer, inconnus. */
export function bilanRapprochement(rapprochements: Rapprochement[]) {
    return {
        reconnus: rapprochements.filter((r) => r.niveau === 'exact' || r.niveau === 'mots' || r.niveau === 'alias').length,
        aConfirmer: rapprochements.filter((r) => r.niveau === 'aConfirmer').length,
        inconnus: rapprochements.filter((r) => r.niveau === 'inconnu').length,
    };
}

/** Le libellé affiché à droite de chaque ligne de l'étape 2. */
export function etiquetteNiveau(rapprochement: Rapprochement): string {
    switch (rapprochement.niveau) {
        case 'exact':
            return rapprochement.nomFichier !== rapprochement.nomFichier.trim() ? 'espace en trop' : 'exact';
        case 'mots':
            return 'ordre inversé';
        case 'alias':
            return 'déjà confirmé';
        case 'aConfirmer':
            return 'à confirmer';
        default:
            return 'ignoré';
    }
}
